import { useEffect, useState, useCallback, useMemo } from "react";
import { IcPageHeader, IcButton, IcSelect, IcEmptyState, IcStatusTag } from "@ukic/react";

import { api, type Decode, type Device, type Preset } from "../api";
import { humanHz, timeAgo } from "../format";
import { DataTable, type Column } from "../components/DataTable";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";
import { useRole } from "../useRole";
import { useToast } from "../toast";

// Survey decoder formats (each emits per-channel `survey` finding records).
const SURVEY_FORMATS = ["spectrum-survey", "gsm-survey", "pocsag-survey", "dmr-survey", "dv-survey", "tetra-survey", "tetrapol-survey", "lte-survey"];
const RTL_WINDOW_HZ = 2_380_000; // a -multi cluster must fit one RTL dongle's window

interface Finding {
  rowId: number;
  device_id: string;
  ts: string;
  protocol: string;
  freq?: number;
  raw: Record<string, unknown>;
}

function toFinding(d: Decode): Finding | null {
  let b: Record<string, unknown>;
  try {
    b = JSON.parse(d.body);
  } catch {
    return null;
  }
  if (b.event !== "survey") return null;
  return {
    rowId: d.id,
    device_id: d.device_id,
    ts: d.ts,
    protocol: String(b.protocol ?? d.format.replace("-survey", "")),
    freq: (b.freq as number | undefined) ?? d.freq ?? undefined,
    raw: b,
  };
}

// A compact, protocol-specific summary for the Detail column.
function detail(f: Finding): string {
  const b = f.raw;
  const parts: string[] = [];
  if (b.service) parts.push(String(b.service));
  if (b.width_khz) parts.push(`${b.width_khz} kHz`);
  if (b.band) parts.push(String(b.band));
  if (b.arfcn != null) parts.push(`ARFCN ${b.arfcn}`);
  if (b.mcc) parts.push(`${b.mcc}-${b.mnc ?? "?"}`);
  if (b.lac != null) parts.push(`LAC ${b.lac}`);
  if (b.cid != null) parts.push(`CID ${b.cid}`);
  if (Array.isArray(b.modes) && b.modes.length) parts.push((b.modes as string[]).join("/"));
  if (Array.isArray(b.nac) && b.nac.length) parts.push(`NAC ${(b.nac as string[]).join(",")}`);
  if (Array.isArray(b.ran) && b.ran.length) parts.push(`RAN ${(b.ran as string[]).join(",")}`);
  if (b.subprotocol) parts.push(String(b.subprotocol));
  if (b.messages) parts.push(`${b.messages} msgs`);
  if (b.address_count) parts.push(`${b.address_count} addrs`);
  if (Array.isArray(b.color_codes) && b.color_codes.length) parts.push(`CC ${b.color_codes.join(",")}`);
  if (b.talkgroup_count) parts.push(`${b.talkgroup_count} TGs`);
  if (b.tetra_messages) parts.push(`${b.tetra_messages} TETRA msgs`);
  if (b.tetrapol_frames) parts.push(`TETRAPOL ${b.tetrapol_frames} frames`);
  if (b.network) parts.push(`net ${b.network}`);
  if (b.pci != null) parts.push(`PCI ${b.pci}`);
  return parts.join(" · ") || "—";
}

// Count of intelligence-bearing identifiers a finding carries (for the badge).
function idCount(f: Finding): number {
  const b = f.raw;
  return (
    (Number(b.imsi_count) || 0) +
    (Number(b.address_count) || 0) +
    (Number(b.talkgroup_count) || 0)
  );
}

export default function Survey() {
  const { isAdmin } = useRole();
  const toast = useToast();
  const [findings, setFindings] = useState<Finding[] | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [deviceId, setDeviceId] = useState("");
  const [protocol, setProtocol] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    Promise.all(
      SURVEY_FORMATS.map((f) =>
        api.listDecodes({ format: f, device_id: deviceId || undefined, limit: 500 }).catch(() => []),
      ),
    )
      .then((lists) => {
        const all = lists.flat().map(toFinding).filter((f): f is Finding => f !== null);
        // Dedupe to the latest finding per (device, protocol, freq) — re-runs stack.
        const seen = new Set<string>();
        const latest: Finding[] = [];
        for (const f of all) {
          const k = `${f.device_id}|${f.protocol}|${f.freq}`;
          if (seen.has(k)) continue;
          seen.add(k);
          latest.push(f);
        }
        latest.sort((a, b) => (a.freq ?? 0) - (b.freq ?? 0));
        setFindings(latest);
      })
      .catch(setError);
  }, [deviceId]);

  const loadPresets = useCallback(() => {
    api.listPresets().then(setPresets).catch(() => setPresets([]));
  }, []);

  useEffect(() => {
    api.listDevices().then(setDevices).catch(() => setDevices([]));
  }, []);
  useEffect(() => {
    load();
    loadPresets();
  }, [load, loadPresets]);

  const deviceName = useMemo(() => {
    const m = new Map(devices.map((d) => [d.id, d.name]));
    return (id: string) => m.get(id) ?? id;
  }, [devices]);
  const deviceOptions = useMemo(
    () => [{ label: "All devices", value: "" }, ...devices.map((d) => ({ label: d.name, value: d.id }))],
    [devices],
  );
  const protocols = useMemo(
    () => Array.from(new Set((findings ?? []).map((f) => f.protocol))).sort(),
    [findings],
  );
  const shown = (findings ?? []).filter((f) => !protocol || f.protocol === protocol);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      loadPresets();
    } catch (e) {
      toast.error("Failed", e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const savePreset = (f: Finding) => {
    const mhz = f.freq ? (f.freq / 1e6).toFixed(4).replace(/0+$/, "").replace(/\.$/, "") : "?";
    const label = `${f.protocol} ${mhz}MHz${detail(f) !== "—" ? ` (${detail(f)})` : ""}`.slice(0, 110);
    return run(
      () =>
        api.createPreset({
          protocol: f.protocol,
          format: f.protocol, // single-channel decoder for this protocol
          label,
          freq: f.freq,
          extra: f.raw,
          source: "survey",
          device_id: f.device_id,
        }),
      `Saved preset: ${label}`,
    );
  };

  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const selectedFindings = shown.filter((f) => selected.has(f.rowId));
  const clusterFreqs = selectedFindings.map((f) => f.freq).filter((x): x is number => !!x);
  const sameProto = new Set(selectedFindings.map((f) => f.protocol)).size === 1;
  const clusterSpan =
    clusterFreqs.length >= 2 ? Math.max(...clusterFreqs) - Math.min(...clusterFreqs) : 0;
  const clusterTooWide = clusterSpan > RTL_WINDOW_HZ;

  const saveCluster = () => {
    if (selectedFindings.length < 2 || !sameProto) return;
    const proto = selectedFindings[0].protocol;
    const lo = Math.min(...clusterFreqs) / 1e6;
    const hi = Math.max(...clusterFreqs) / 1e6;
    const label = `${proto} cluster ${lo.toFixed(3)}–${hi.toFixed(3)}MHz (${clusterFreqs.length} ch)`;
    return run(
      () =>
        api.createPreset({
          protocol: proto,
          format: proto, // channelizing decoders (e.g. pocsag/acars) infer multi from >1 channel
          label,
          channels: clusterFreqs,
          extra: { span_hz: clusterSpan },
          source: "survey",
        }),
      `Saved cluster: ${label}`,
    ).then(() => setSelected(new Set()));
  };

  const columns: Column<Finding>[] = [
    {
      key: "sel",
      header: "",
      cell: (f) =>
        f.freq ? (
          <input type="checkbox" aria-label="select" checked={selected.has(f.rowId)} onChange={() => toggle(f.rowId)} />
        ) : null,
    },
    { key: "device", header: "Site", cell: (f) => deviceName(f.device_id) },
    { key: "protocol", header: "Protocol", cell: (f) => <code>{f.protocol}</code> },
    { key: "freq", header: "Freq", cell: (f) => (f.freq ? humanHz(f.freq) : "—") },
    {
      key: "sig",
      header: "Signal",
      cell: (f) => {
        const s = f.raw.snr ?? f.raw.power_db ?? f.raw.power;
        return s != null ? String(s) : "—";
      },
    },
    { key: "detail", header: "Detail", cell: (f) => detail(f) },
    {
      key: "ids",
      header: "IDs",
      cell: (f) => {
        const n = idCount(f);
        return n ? <IcStatusTag label={String(n)} status="success" /> : <span className="hl-muted">0</span>;
      },
    },
    { key: "ts", header: "Seen", cell: (f) => timeAgo(f.ts) },
    {
      key: "save",
      header: "",
      cell: (f) =>
        isAdmin && f.freq ? (
          <IcButton size="small" variant="secondary" disabled={busy} onClick={() => savePreset(f)}>
            Save preset
          </IcButton>
        ) : null,
    },
  ];

  const presetCols: Column<Preset>[] = [
    { key: "label", header: "Label", cell: (p) => p.label },
    { key: "protocol", header: "Target", cell: (p) => <code>{p.format ?? p.protocol}</code> },
    {
      key: "ch",
      header: "Freq / channels",
      cell: (p) =>
        p.channels && p.channels.length
          ? p.channels.map((c) => (c / 1e6).toFixed(3)).join(", ") + " MHz"
          : p.freq
            ? humanHz(p.freq)
            : "—",
    },
    { key: "source", header: "Source", cell: (p) => p.source ?? "—" },
    {
      key: "del",
      header: "",
      cell: (p) =>
        isAdmin ? (
          <IcButton size="small" variant="destructive" disabled={busy} onClick={() => run(() => api.deletePreset(p.id), "Preset deleted")}>
            Delete
          </IcButton>
        ) : null,
    },
  ];

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading="Survey" subheading="Discovered channels per site — save the good ones as decoder presets">
        <IcButton slot="actions" variant="secondary" onClick={load}>
          Refresh
        </IcButton>
      </IcPageHeader>

      <div className="hl-panel hl-row">
        <IcSelect label="Site" value={deviceId} options={deviceOptions}
          onIcChange={(e) => setDeviceId((e as CustomEvent<{ value: string }>).detail.value)} />
        <IcSelect label="Protocol" value={protocol}
          options={[{ label: "All protocols", value: "" }, ...protocols.map((p) => ({ label: p, value: p }))]}
          onIcChange={(e) => setProtocol((e as CustomEvent<{ value: string }>).detail.value)} />
        {isAdmin && selectedFindings.length >= 2 && (
          <>
            <IcButton onClick={saveCluster} disabled={busy || !sameProto || clusterTooWide}
              variant={clusterTooWide || !sameProto ? "destructive" : "primary"}>
              Save {selectedFindings.length} as cluster
            </IcButton>
            <span className={clusterTooWide ? "hl-projection hl-projection--over" : "hl-muted"}>
              {!sameProto ? "select one protocol" : `span ${humanHz(clusterSpan)}${clusterTooWide ? " — exceeds one RTL window" : " — fits one dongle"}`}
            </span>
          </>
        )}
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : findings === null ? (
        <Loading label="Loading survey findings…" />
      ) : shown.length === 0 ? (
        <IcEmptyState
          heading="No survey findings yet"
          subheading="Run a survey decoder (Device → Decoder → e.g. 'POCSAG/FLEX site survey'). Discovered channels appear here."
        />
      ) : (
        <DataTable caption="Survey findings" columns={columns} rows={shown} rowKey={(f) => String(f.rowId)} />
      )}

      <div className="hl-panel hl-stack">
        <h2>Saved presets</h2>
        <p className="hl-muted">Loadable from Device → Decoder to prefill freq (single) or a channel cluster (-multi).</p>
        {presets.length === 0 ? (
          <span className="hl-muted">None yet — save a finding above.</span>
        ) : (
          <DataTable caption="Saved presets" columns={presetCols} rows={presets} rowKey={(p) => String(p.id)} />
        )}
      </div>
    </div>
  );
}
